<script setup lang="ts">
import axios from 'axios'

import ImportDetailModal from '@/views/import/Components/ImportDetailModal.vue'
import type { CustomerImport, CustomerImportDiagnostics } from '@/types/types'

const props = defineProps<{
  customerImport: CustomerImport
  isLaunching?: boolean
}>()

const emit = defineEmits<{
  launch: []
}>()

const { t } = useI18n()
const notification = useNotification()

const diagnostics = ref<CustomerImportDiagnostics | null>(null)
const isLoadingDiagnostics = ref(false)
const detailPollTimer = ref<ReturnType<typeof setInterval> | null>(null)

const DETAIL_POLL_INTERVAL_MS = 4000

const isPreparationComplete = computed(() => {
  if (!diagnostics.value) {
    return false
  }

  const { expectedLines, parsedLines, isComplete } = diagnostics.value

  return (expectedLines > 0 && parsedLines >= expectedLines) || isComplete
})

const stopDetailPolling = (): void => {
  if (detailPollTimer.value !== null) {
    clearInterval(detailPollTimer.value)
    detailPollTimer.value = null
  }
}

const fetchDiagnostics = async (importId: number, initial = false): Promise<boolean> => {
  const { data } = await axios.get(`customer-imports/${importId}/diagnostics`, {
    params: initial ? { initial: 1 } : undefined
  })

  diagnostics.value = data.data

  if (data.data.isComplete) {
    return false
  }

  const parsingComplete = data.data.expectedLines > 0 && data.data.parsedLines >= data.data.expectedLines

  return !parsingComplete || Boolean(data.data.startedAt)
}

const startDetailPolling = (importId: number): void => {
  stopDetailPolling()

  detailPollTimer.value = setInterval(async () => {
    try {
      const shouldContinue = await fetchDiagnostics(importId)

      if (!shouldContinue) {
        stopDetailPolling()
      }
    } catch {
      stopDetailPolling()
    }
  }, DETAIL_POLL_INTERVAL_MS)
}

const loadDiagnostics = async (): Promise<void> => {
  isLoadingDiagnostics.value = true

  try {
    const shouldPoll = await fetchDiagnostics(props.customerImport.id, true)

    if (shouldPoll) {
      startDetailPolling(props.customerImport.id)
    }
  } catch {
    notification.create('error', {
      text: t('pages.import.historyPage.diagnosticsModal.loadError')
    })
  } finally {
    isLoadingDiagnostics.value = false
  }
}

const downloadImportErrors = async (importId: number, fileName: string) => {
  try {
    const { data } = await axios.get(`customer-imports/${importId}/errors/export`, { responseType: 'blob' })
    const fileURL = window.URL.createObjectURL(new Blob([data]))
    const fileLink = document.createElement('a')
    const baseName = fileName.replace(/\.[^.]+$/, '')

    fileLink.href = fileURL
    fileLink.setAttribute('download', `${baseName}-errors.csv`)
    document.body.appendChild(fileLink)
    fileLink.click()
    fileLink.remove()
    URL.revokeObjectURL(fileURL)

    notification.create('success', {
      text: t('pages.import.historyPage.diagnosticsModal.errorsExportSuccess'),
      timeout: 2000
    })
  } catch {
    notification.create('error', {
      text: t('pages.import.historyPage.diagnosticsModal.errorsExportError')
    })
  }
}

const downloadImportFile = async (importId: number, fileName: string) => {
  try {
    const { data } = await axios.get(`customer-imports/${importId}/file`, { responseType: 'blob' })
    const fileURL = window.URL.createObjectURL(new Blob([data]))
    const fileLink = document.createElement('a')

    fileLink.href = fileURL
    fileLink.setAttribute('download', fileName)
    document.body.appendChild(fileLink)
    fileLink.click()
    fileLink.remove()
    URL.revokeObjectURL(fileURL)

    notification.create('success', {
      text: t('global.download.success'),
      timeout: 2000
    })
  } catch {
    notification.create('error', {
      text: t('pages.import.historyPage.notifications.fileDownloadError')
    })
  }
}

onMounted(() => {
  loadDiagnostics()
})

onUnmounted(() => {
  stopDetailPolling()
})
</script>

<template>
  <div class="flex flex-col gap-6 text-left">
    <div class="flex flex-col gap-2">
      <h2 class="text-lg font-medium">{{ t('pages.import.importPage.finalize.title') }}</h2>
      <p class="text-sm text-muted-foreground">
        {{
          isPreparationComplete
            ? t('pages.import.importPage.finalize.subtitleReady')
            : t('pages.import.importPage.finalize.subtitleAnalyzing')
        }}
      </p>
    </div>

    <ImportDetailModal
      :import-id="customerImport.id"
      :diagnostics="diagnostics"
      :is-loading="isLoadingDiagnostics"
      :is-launching="isLaunching"
      show-launch-actions
      @download="downloadImportFile"
      @export-errors="downloadImportErrors"
      @launch="emit('launch')"
    />
  </div>
</template>
